import { ENV } from 'utils/env';
import { rest } from 'msw';
import {
    discountRecapFixtureFactory,
    discountStrategyFixtureFactory,
} from '@innovorder/order_detail/dist/fixture_factory/order_recap.fixture_factory';
import { ConsumptionModeType, DISCOUNT_TYPE } from '@innovorder/order_detail';
import { DateTime } from 'luxon';
import { ChannelId, OrderMainStatus } from 'services/order/order.type';
import { weborderingConfigurationFixtureFactory } from '../factories/weborderingConfiguration';
import { menuFixtureFactory } from '../factories/menu';
import {
    omnicanalOrderFixtureFactory,
    orderCreatedFixtureFactory,
    orderVerifiedFixtureFactory,
} from '../factories/order';
import {
    multiOrderCartErrorPayload,
    multiOrderMultiErrorExpectedAtTooEarly,
    mutiOrderErrorWith3Baskets,
    mutiOrderErrorWithMultiBasket,
    orderMock,
    singleOrderCartErrorPayload,
    singleSimpleOrderCartErrorPayload,
} from '../factories/order/mock';
import { consumptionModeFixtureFactory } from '../factories/consumptionMode';
import { weborderingRestaurantFixtureFactory } from '../factories/weborderingRestaurant';
import { weborderingRestaurantFamilyFixtureFactory } from '../factories/weborderingRestaurantFamily';

export const MENUID_THROWING_ERROR_ON_PRODUCT_1 = 4001;
export const MENUID_THROWING_ERROR_ON_PRODUCT_2 = 4002;
export const RESTAURANTID_ERROR_PREVIEW_CREATE = 5;
export const RESTAURANTID_MENU_NOT_ACTIVATED = 12;
export const RESTAURANTID_ERROR_CREATE = 2;
export const RESTAURANTID_ERROR_STRIPE_DECLINED = 6;
export const RESTAURANTID_ERROR_EXPECTED_AT_TOO_EARLY = 7;
export const RESTAURANTID_FETCH_ERROR = 8;

export const RESTAURANTID_NO_CONSUMPTION_MODE = 10;
export const RESTAURANTID_TWO_CONSUMPTION_MODES = 11;

export const handlers = [
    rest.get(`${ENV.API_URL}/brands/webordering-configuration/:brandHash`, (req, res, ctx) => {
        if (
            !req.params.brandHash ||
            ['error', 'no-brand'].includes(req.params.brandHash as string)
        ) {
            return res(ctx.status(400), ctx.json({ message: `Unknown brand` }));
        }
        return res(
            ctx.json({
                data: weborderingConfigurationFixtureFactory(),
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/brand/:brandHash/restaurants`, (req, res, ctx) => {
        return res(
            ctx.json({
                data: [
                    weborderingRestaurantFixtureFactory({
                        brandRestaurantFamilies: [{ brandRestaurantFamilyId: 1 }],
                    }),
                ],
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/brand/:brandHash/restaurants_families`, (req, res, ctx) => {
        return res(
            ctx.json({
                data: [weborderingRestaurantFamilyFixtureFactory({ brandRestaurantFamilyId: 1 })],
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/menus/:menuId/hash_map`, (req, res, ctx) => {
        if (!req.params.menuId || ['no-menu'].includes(req.params.menuId as string)) {
            return res(ctx.status(400), ctx.json({ message: `Unknown menu` }));
        }
        return res(ctx.status(200), ctx.json({ data: menuFixtureFactory() }));
    }),

    rest.get(`${ENV.API_URL}/consumption_modes`, async (req, res, ctx) => {
        const requestedUrl = req.url.toString();
        const isEmpty = requestedUrl.includes(`restaurant_id=${RESTAURANTID_NO_CONSUMPTION_MODE}`);
        const hasTwoModes = requestedUrl.includes(
            `restaurant_id=${RESTAURANTID_TWO_CONSUMPTION_MODES}`,
        );

        if (isEmpty) return res(ctx.status(200), ctx.json({ data: [] }));
        if (hasTwoModes)
            return res(
                ctx.status(200),
                ctx.json({
                    data: [
                        consumptionModeFixtureFactory(),
                        consumptionModeFixtureFactory({ type: ConsumptionModeType.MODE_TAKE_AWAY }),
                    ],
                }),
            );
        return res(ctx.status(200), ctx.json({ data: [consumptionModeFixtureFactory()] }));
    }),

    rest.post(`${ENV.API_URL}/orders/preview`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.restaurantId === RESTAURANTID_ERROR_PREVIEW_CREATE) {
            return res(ctx.status(400), ctx.json(singleSimpleOrderCartErrorPayload));
        }

        if (
            body.menuId === MENUID_THROWING_ERROR_ON_PRODUCT_1 &&
            body.cart.map(({ productId }: { productId: number }) => productId).includes(1)
        ) {
            return res(ctx.status(400), ctx.json(singleOrderCartErrorPayload));
        }

        if (body.promocode && body.promocode.includes('error')) {
            return res(
                ctx.status(400),
                ctx.json({
                    status: 400,
                    code: 'invalid_order_payload',
                    message: 'invalid order payload',
                    extraData: {
                        errors: [
                            {
                                status: 400,
                                message: 'wrong promocode',
                                code: 'promocode_not_found',
                                extraData: {},
                            },
                        ],
                        restaurantId: body.restaurantId,
                    },
                }),
            );
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: orderVerifiedFixtureFactory(body.restaurantId === 1 ? body : orderMock, [
                    discountRecapFixtureFactory({
                        strategy: discountStrategyFixtureFactory({
                            type: DISCOUNT_TYPE.PROMOCODE,
                        }),
                    }),
                ]),
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/multi-orders/preview`, async (req, res, ctx) => {
        const body = await req.json();

        const menus = body.restaurantCarts.map(({ menuId }: { menuId: number }) => menuId);
        const carts = body.restaurantCarts.map(({ cart }: { cart: unknown }) => cart).flat();
        const restaurants = body.restaurantCarts.map(
            ({ restaurantId }: { restaurantId: number }) => restaurantId,
        );

        if (restaurants.includes(RESTAURANTID_ERROR_PREVIEW_CREATE)) {
            return res(ctx.status(400), ctx.json(multiOrderCartErrorPayload));
        }

        if (
            menus.includes(MENUID_THROWING_ERROR_ON_PRODUCT_1) &&
            carts.map(({ productId }: { productId: number }) => productId).includes(1)
        ) {
            return res(ctx.status(400), ctx.json(multiOrderCartErrorPayload));
        }

        if (
            menus.includes(MENUID_THROWING_ERROR_ON_PRODUCT_2) &&
            carts.map(({ productId }: { productId: number }) => productId).includes(3)
        ) {
            return res(ctx.status(400), ctx.json(mutiOrderErrorWith3Baskets));
        }

        if (
            menus.includes(MENUID_THROWING_ERROR_ON_PRODUCT_2) &&
            carts.map(({ productId }: { productId: number }) => productId).includes(2)
        ) {
            return res(ctx.status(400), ctx.json(mutiOrderErrorWithMultiBasket));
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    ...body,
                    verifiedOrders: body?.restaurantCarts ?? [],
                },
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/orders`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.restaurantId === RESTAURANTID_ERROR_CREATE) {
            return res(ctx.status(400), ctx.json({ error: `error` }));
        }

        if (body.restaurantId === RESTAURANTID_MENU_NOT_ACTIVATED) {
            return res(
                ctx.status(400),
                ctx.json({
                    status: 400,
                    code: 'menu_not_activated',
                    message: 'menu_not_activated',
                }),
            );
        }

        if (body.restaurantId === 3) {
            return res(
                ctx.status(200),
                ctx.json({
                    data: orderCreatedFixtureFactory({
                        ...body,
                        requiredAction: {
                            orderUuid: 'abc-123',
                            intentId: '1',
                            payment_intent_client_secret: '1',
                            requires_action: true,
                        },
                    }),
                }),
            );
        }

        if (body.restaurantId === 4 || body.restaurantId === 5) {
            return res(ctx.status(400), ctx.json(singleOrderCartErrorPayload));
        }

        if (body.restaurantId === RESTAURANTID_ERROR_STRIPE_DECLINED) {
            return res(
                ctx.status(403),
                ctx.json({
                    status: 403,
                    code: 'stripe_card_declined',
                    message: 'stripe_card_declined_message',
                }),
            );
        }

        if (body.restaurantId === RESTAURANTID_ERROR_EXPECTED_AT_TOO_EARLY) {
            return res(
                ctx.status(403),
                ctx.json({
                    status: 403,
                    code: 'expected_at_too_early',
                    extraData: { delay: 20, restaurantId: body.restaurantId },
                }),
            );
        }

        if (body.restaurantId === RESTAURANTID_FETCH_ERROR) {
            throw new Error('Fetch error');
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    ...orderCreatedFixtureFactory(body.restaurantId === 1 ? body : orderMock),
                    mainStatus: OrderMainStatus.ORDER_PLACED,
                },
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/multi-orders`, async (req, res, ctx) => {
        const body = await req.json();

        const restaurantsId = body.restaurantCarts.map(
            ({ restaurantId }: { restaurantId: number }) => restaurantId,
        );

        if (restaurantsId.includes(2)) {
            return res(ctx.status(400), ctx.json({ error: `error` }));
        }

        if (restaurantsId.includes(3)) {
            return res(
                ctx.status(200),
                ctx.json({
                    data: orderCreatedFixtureFactory({
                        ...body,
                        multiOrderId: 'abc-123',
                        multiCreatedOrders: [{ orderUuid: 'abc-123', restaurantId: 3 }],
                        requiredAction: {
                            intentId: '1',
                            payment_intent_client_secret: '1',
                            requires_action: true,
                        },
                        mainStatus: OrderMainStatus.ORDER_PLACED,
                    }),
                }),
            );
        }

        if (restaurantsId.includes(4) || restaurantsId.includes(5)) {
            return res(ctx.status(400), ctx.json(multiOrderCartErrorPayload));
        }

        if (restaurantsId.includes(RESTAURANTID_ERROR_EXPECTED_AT_TOO_EARLY)) {
            return res(ctx.status(403), ctx.json(multiOrderMultiErrorExpectedAtTooEarly.data));
        }

        if (restaurantsId.includes(RESTAURANTID_FETCH_ERROR)) {
            throw new Error('Fetch error');
        }

        if (restaurantsId.includes(RESTAURANTID_MENU_NOT_ACTIVATED)) {
            return res(
                ctx.status(400),
                ctx.json({
                    status: 400,
                    code: 'menu_not_activated',
                    message: 'menu_not_activated',
                }),
            );
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    ...body,
                    multiOrderId: 'abc-123',
                    mainStatus: OrderMainStatus.ORDER_PLACED,
                },
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/orders/confirm`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.orderId === 2 || body.orderUuid === '22222222-2222-2222-2222-222222222222') {
            return res(ctx.status(400), ctx.json({ error: `error` }));
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: orderCreatedFixtureFactory(body.restaurantId === 1 ? body : orderMock),
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/multi-orders/confirm`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.multiOrderId === 'def-456') {
            return res(ctx.status(400), ctx.json({ error: `error` }));
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: [orderCreatedFixtureFactory(body.restaurantId === 1 ? body : orderMock)],
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/v1/orders`, async (req, res, ctx) => {
        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    omnichannelOrders: [
                        {
                            restaurant: { name: 'Order History - Restaurant #1' },
                            channelId: ChannelId.KIOSK,
                            omnichannelOrderId: 1,
                            startDate: '2025-02-18T16:50:39.000Z',
                        },
                        {
                            restaurant: { name: 'Order History - Restaurant #2' },
                            channelId: ChannelId.WEB,
                            omnichannelOrderId: 2,
                            startDate: '2025-02-18T16:50:39.000Z',
                        },
                        {
                            restaurant: { name: 'Order History - Restaurant #3' },
                            channelId: '3',
                            omnichannelOrderId: 3,
                            startDate: '2025-02-18T16:50:39.000Z',
                        },
                        {
                            restaurant: { name: 'Order History - Restaurant #4' },
                            channelId: ChannelId.WEB,
                            omnichannelOrderId: 3,
                            startDate: '2025-02-18T16:50:39.000Z',
                            status: 'CONTRA_ENTRY',
                            reportOrderContraEntry: {
                                originOrderDate: '2025-02-19T16:50:39.000Z',
                            },
                        },
                    ],
                },
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/v1/orders/:orderId`, async (req, res, ctx) => {
        if (req.params.orderId === 'null') {
            return res(ctx.status(400), ctx.json({ data: null }));
        }

        if (req.params.orderId === 'error') {
            return res(ctx.status(400), ctx.json({ data: { error: `error` } }));
        }

        const legacyCartRawData =
            '{"price":1400,"vatPrice":1400000,"luncheonEligibleAmount":1400,"products":[{"productId":6755058528701671,"signature":"34f92b4a-3615-4a55-be99-664e8cf98a48","isProducible":false,"isEligibleLuncheon":true,"version":1,"name":"formule burger nature","quantity":1,"price":1400,"vatPrice":1400000,"unitPrice":1200,"groupName":null,"steps":[{"stepId":1492218,"signature":"e591815a-7701-4a41-8323-b54bd8fa0b38","version":1,"name":"sup payant obligatoire","products":[{"productId":6755058528701536,"signature":"731de36f-6560-4a31-b1e1-81e3265a2a67","isProducible":false,"isEligibleLuncheon":true,"version":1,"name":"Double fromage","quantity":1,"price":200,"vatPrice":200000,"unitPrice":200,"groupName":null,"steps":[],"tags":{"129439":{"tagId":129439,"name":"All Burger"}},"vat":1000,"sku":null,"billingCode":null,"purchasePrice":0,"pos":null,"originalUnitPrice":0,"externalReferences":{}}],"vatPrice":200000,"price":200,"quantity":1,"type":1,"ticketLabel":null}],"tags":{},"vat":1000,"sku":null,"billingCode":null,"purchasePrice":0,"pos":null,"originalUnitPrice":1200,"categoriesNames":["FORMULES"],"externalReferences":{}}],"quantityPerProductId":{"6755058528701671":1,"6755058528701536":1}}';
        const enrichedCartRawData =
            '{"enrichedCart":[{"productId":6755058528289265,"productCartId":"f0bcdd19-fabd-488b-b228-d4baca7a5be8","quantity":1,"steps":[],"name":"Festin ?","originalUnitPrice":500,"price":500,"unitPrice":500,"vat":1700,"externalReferences":{},"tags":{"278328":{"tagId":278328,"name":"ADMISSIONNABLE"},"278329":{"tagId":278329,"name":"SUBVENTIONNABLE"}},"servingCount":0,"isEligibleLuncheon":false,"isProducible":false,"imageUrl":"https://static-dev.innovorder.fr/uploads/45c74cb95c47/914c07e2ac2c1aa5b91675550a16b529.png","pos":{"alwaysAskForCustomization":false,"barCode":"452212554","description":"<p>Un délicieux festin&nbsp;?</p>","discountable":true,"isDeposit":false,"isMenu":false,"minQuantity":3,"name":"Festin ?","price":500,"productId":6755058528289265,"quantity":-1,"ticketLabel":"Festin ?"},"sku":{"skuId":2533873,"brandId":341,"value":"123","deliveryVat":1000,"driveVat":1000,"takeAwayVat":1000,"sitInVat":1000}},{"productId":6755058528969582,"productCartId":"5be624e7-5baa-4a66-82b3-407a909ed3ca","quantity":1,"steps":[{"stepId":1477950,"name":"Une petite sauce artisanale ?","ticketLabel":null,"products":[{"productId":6755058528289122,"productCartId":"637a3ae5-747f-4585-b525-af1399515197","quantity":1,"steps":[],"unitPrice":0,"name":"Sauce Mémère","originalUnitPrice":0,"price":0,"vat":1000,"externalReferences":{},"tags":{},"servingCount":0,"isEligibleLuncheon":false,"isProducible":false,"pos":{"alwaysAskForCustomization":false,"barCode":null,"description":"","discountable":true,"isDeposit":false,"isMenu":false,"minQuantity":0,"name":"Sauce Mémère","price":0,"productId":6755058528289122,"quantity":-1,"ticketLabel":"Sauce Mémère"},"sku":null}]}],"name":"Smash Fernand - les sauces","originalUnitPrice":300,"price":300,"unitPrice":300,"vat":1000,"externalReferences":{},"tags":{},"servingCount":0,"isEligibleLuncheon":false,"isProducible":false,"imageUrl":"https://static-dev.innovorder.fr/uploads/45c74cb95c47/d3ab4c83b9ae51bf8c630200ed746334.jpg","pos":{"alwaysAskForCustomization":false,"barCode":null,"description":"<p>Sous-étape des sauces uniquement</p>","discountable":false,"isDeposit":false,"isMenu":false,"minQuantity":1,"name":"Smash Fernand - les sauces","price":300,"productId":6755058528969582,"quantity":-1,"ticketLabel":"Smash Fernand - les sauces"},"sku":null}],"version":"enriched-cart-1"}';

        const cartRawData =
            req.params.orderId === 'legacy' ? legacyCartRawData : enrichedCartRawData;

        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    startDate: '2025-02-18T16:50:39.000Z',
                    channelId: '2',
                    totalPriceDiscountedWithTaxIncluded: 800,
                    totalPriceWithTaxIncluded: 800,
                    totalPriceWithTaxExcluded: 700,
                    totalDiscount: 0,
                    currency: 'EUR',
                    totalTax: 100,
                    totalPriceDiscountedWithTaxExcluded: 700,
                    ticketNumber: '1',
                    orderUuid: '123',
                    taxes: [
                        {
                            taxRate: 1700,
                            totalTax: 73,
                            totalInclTax: 500,
                            totalExclTax: 427,
                        },
                        {
                            taxRate: 1000,
                            totalTax: 27,
                            totalInclTax: 300,
                            totalExclTax: 273,
                        },
                    ],
                    items: [
                        {
                            itemId: 6755058529004669,
                            name: 'Festin ?',
                            type: 'Product',
                            status: 'PAID',
                            quantity: 1,
                            totalPriceWithTaxIncluded: 500,
                            originalUnitPrice: 500,
                            itemType: 'PRODUCT',
                            parentItemId: null,
                            vatValue: 1700,
                            skuValue: '123',
                            free: false,
                            purchasePrice: null,
                            billingCode: null,
                            discounted: false,
                            reduced: false,
                            service: 1,
                            totalItemDiscounted: 0,
                            totalPriceDiscounted: 0,
                            cancelReason: null,
                            deviceName: 'WEB',
                            deviceId: 'IO_WEB',
                            tags: [
                                {
                                    value: 'SUBVENTIONNABLE',
                                },
                                {
                                    value: 'ADMISSIONNABLE',
                                },
                            ],
                            categories: [
                                {
                                    value: '? Formule',
                                },
                            ],
                        },
                    ],
                    restaurant: {
                        name: 'SAU',
                    },
                    grants: [],
                    entranceFees: [],
                    fees: [],
                    discounts: [],
                    cartRawData,
                },
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/v1/orders/uuid/:orderUuid`, async (req, res, ctx) => {
        if (req.params.orderUuid === 'abc-123') {
            return res(
                ctx.status(200),
                ctx.json({
                    data: {
                        orderUuid: req.params.orderUuid,
                        restaurantId: 1,
                        tableName: '10',
                        dailyOrderId: 41,
                        restaurant: {},
                        cartRawData: '{"enrichedCart": []}',
                    },
                }),
            );
        }
        return res(
            ctx.status(200),
            ctx.json({
                data: omnicanalOrderFixtureFactory({
                    tableName: '10',
                    discounts: [
                        {
                            name: 'NOEL',
                            value: 100,
                        },
                    ],
                }),
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/multi-orders/:multiOrderUuid`, async (req, res, ctx) => {
        const { multiOrderUuid } = req.params;
        const isNewOrder = multiOrderUuid.includes('new-order');
        const isProcessed = multiOrderUuid.includes('order-processed');

        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    multiOrderUuid,
                    createdAt: isNewOrder ? DateTime.now().toISO() : '2023-10-18T12:34:42.502Z',
                    orders: [
                        {
                            orderId: 10,
                            tableName: '10',
                            dailyOrderId: 41,
                            restaurantId: 1,
                            restaurant: { name: 'Big Fernand' },
                            cartRawData: '{"enrichedCart": []}',
                            mainStatus: isProcessed ? OrderMainStatus.ORDER_PROCESSED : undefined,
                        },
                    ],
                },
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/orders/receipt`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.email === 'error@message.com') {
            return res(ctx.status(400), ctx.json({ status: 400, message: `error_message` }));
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: undefined,
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/multi-orders/receipt`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.email === 'error@message.com') {
            return res(ctx.status(400), ctx.json({ status: 400, message: `error_message` }));
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: undefined,
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/oauth/login`, async (req, res, ctx) => {
        const body = await req.json();

        if (body.autoLoginJWT === 'fail-auto-login-jwt') {
            return res(
                ctx.status(401),
                ctx.json({
                    status: 401,
                    code: 'login_failed',
                    message: `Your identifiers are invalid.`,
                }),
            );
        }

        if (body.autoLoginJWT === 'success-auto-login-jwt') {
            return res(
                ctx.status(200),
                ctx.json({
                    data: {
                        accessToken: 'accessToken',
                        customer: {
                            firstName: 'John',
                            lastName: 'Doe',
                            email: 'john.doe@success.com',
                            phone: '0601020304',
                            customerId: 1,
                        },
                    },
                }),
            );
        }

        if (body.username === 'error@message.com') {
            return res(
                ctx.status(401),
                ctx.json({
                    status: 401,
                    code: 'login_failed',
                    message: `Your identifiers are invalid.`,
                }),
            );
        }

        if (body.username === 'outside@access.dates') {
            return res(
                ctx.status(200),
                ctx.json({
                    data: {
                        accessToken: 'accessTokenOutsideAccessDates',
                        customer: {
                            firstName: 'John',
                            lastName: 'Doe',
                            email: 'outside@access.dates',
                            phone: '0601020304',
                            customerId: 50,
                        },
                    },
                }),
            );
        }

        if (body.username === 'no@channel.access') {
            return res(
                ctx.status(200),
                ctx.json({
                    data: {
                        accessToken: 'accessTokenNoChannelAccess',
                        customer: {
                            firstName: 'John',
                            lastName: 'Doe',
                            email: 'no@channel.access',
                            phone: '0601020304',
                            customerId: 100,
                        },
                    },
                }),
            );
        }

        return res(
            ctx.status(200),
            ctx.json({
                data: {
                    accessToken: 'accessToken',
                    customer: {
                        firstName: 'John',
                        lastName: 'Doe',
                        email: 'john.doe@success.com',
                        phone: '0601020304',
                        customerId: 1,
                    },
                },
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/customers/:customerId/channel/:channelId`, async (req, res, ctx) => {
        const accessToken = req.headers.get('Authorization');
        if (accessToken === 'Bearer accessTokenNoChannelAccess') {
            return res(
                ctx.status(403),
                ctx.json({
                    status: 403,
                    code: 'customer_has_not_access_to_this_channel',
                    message: `You don't have access to this channel.`,
                }),
            );
        }
        if (accessToken === 'Bearer accessTokenOutsideAccessDates') {
            return res(
                ctx.status(403),
                ctx.json({
                    status: 403,
                    code: 'customer_is_outside_his_access_dates',
                    message: `You are outside your access dates.`,
                }),
            );
        }
        return res(ctx.status(200), ctx.json({ data: { hasAccessToChannel: true } }));
    }),

    rest.get(`${ENV.API_URL}/oauth/me`, async (req, res, ctx) => {
        const bearerToken = req.headers.get('Authorization');

        if (bearerToken === 'Bearer expiredAccessToken') {
            return res(
                ctx.status(400),
                ctx.json({
                    status: 400,
                    code: 'permission_denied',
                    message: "Vous n'avez pas la permission pour accéder à cette ressource.",
                }),
            );
        }

        return res(
            ctx.status(200),
            ctx.json({
                code: 'user_succeed',
                message: 'Vous pouvez accéder à cet utilisateur',
                data: {
                    firstName: 'John',
                    lastName: 'Doe',
                    email: 'john.doe@success.com',
                    phone: '0601020304',
                    customerId: 1,
                },
                status: 200,
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/oauth/logout`, async (req, res, ctx) => {
        return res(
            ctx.status(200),
            ctx.json({
                code: 'logout_succeed',
                message: 'Logout succeeded.',
                status: 200,
            }),
        );
    }),

    rest.get(`${ENV.API_URL}/customers/:customerId/balance`, async (req, res, ctx) => {
        return res(
            ctx.status(200),
            ctx.json({
                status: 200,
                code: 'balance_succeed',
                message: 'Vous avez accès à ce contenu.',
                data: {
                    customerBalance: 0,
                },
            }),
        );
    }),

    rest.post(`${ENV.API_URL}/customers/forgottenPassword`, async (req, res, ctx) =>
        res(ctx.status(200), ctx.json({ status: 200, message: `forgotten_password_mail_sent` })),
    ),

    rest.get('/*', (req) => {
        return req.passthrough();
    }),

    rest.post('https://*.ingest.sentry.io/*', (req, res, ctx) => {
        return res(ctx.json(null));
    }),

    rest.post(`${ENV.ELASTIC_APM_SERVER_URL}/*`, (req, res, ctx) => {
        return res(ctx.json(null));
    }),
];
